import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';

// ─── VCD Parser ───────────────────────────────────────────────────────────────
function parseVCD(vcdContent) {
    const signals = {};   // symbol → { name, width, values: [{time, val}] }
    const scopes  = [];   // scope stack for hierarchical names
    let currentTime = 0;
    let maxTime = 0;
    let inDefs = true;

    for (let raw of vcdContent.split('\n')) {
        const line = raw.trim();
        if (!line) continue;

        if (inDefs) {
            if (line.startsWith('$scope'))  { scopes.push(line.split(/\s+/)[2]); continue; }
            if (line.startsWith('$upscope')){ scopes.pop(); continue; }
            if (line.startsWith('$var')) {
                const p = line.split(/\s+/).filter(Boolean);
                // $var type width symbol name [$end]
                const width  = parseInt(p[2]) || 1;
                const symbol = p[3];
                const name   = p[4];
                signals[symbol] = { name, width, values: [], symbol };
                continue;
            }
            if (line.startsWith('$enddefinitions')) { inDefs = false; continue; }
            continue;
        }

        if (line.startsWith('#')) {
            currentTime = parseInt(line.slice(1));
            maxTime = Math.max(maxTime, currentTime);
        } else if (line.startsWith('b') || line.startsWith('B')) {
            const p = line.split(/\s+/).filter(Boolean);
            if (p.length >= 2) {
                const val = p[0].slice(1);
                const sym = p[1];
                if (signals[sym]) signals[sym].values.push({ time: currentTime, val });
            }
        } else if (line.length >= 2) {
            const val = line[0];
            const sym = line.slice(1).trim();
            if (signals[sym]) signals[sym].values.push({ time: currentTime, val });
        }
    }

    // Determine time unit suffix
    let unit = 'ns';

    return { signals, maxTime, unit };
}

// Get signal value at a given time
function getValueAt(values, time) {
    if (!values || values.length === 0) return '?';
    let v = values[0].val;
    for (const e of values) {
        if (e.time > time) break;
        v = e.val;
    }
    return v;
}

// Convert binary string to hex
function binToHex(bin) {
    if (!bin || bin === '?' || /[xzXZ]/.test(bin)) return bin;
    const n = parseInt(bin, 2);
    return isNaN(n) ? bin : n.toString(16).toUpperCase();
}

// ─── Color palette ────────────────────────────────────────────────────────────
const SIGNAL_COLORS = [
    '#60a5fa', '#34d399', '#f59e0b', '#c084fc', '#fb7185',
    '#22d3ee', '#a3e635', '#f97316', '#e879f9', '#2dd4bf'
];

// ─── Constants ────────────────────────────────────────────────────────────────
const ROW_H     = 44;
const LABEL_W   = 170;
const HEADER_H  = 36;
const PAD_RIGHT = 40;

// ─── Main Component ───────────────────────────────────────────────────────────
const WaveformViewer = ({ vcdContent, theme, viewerType = 'Surfer Viewer', formatType = 'VCD' }) => {
    const [zoom, setZoom]         = useState(1);
    const [cursorTime, setCursor] = useState(null);
    const [scrollLeft, setScroll] = useState(0);
    const waveRef = useRef(null);
    const containerRef = useRef(null);

    const data = useMemo(() => {
        if (!vcdContent) return null;
        return parseVCD(vcdContent);
    }, [vcdContent]);

    const { signals, maxTime, unit } = data || { signals: {}, maxTime: 0, unit: 'ns' };
    const signalList = Object.values(signals);
    const timeScale  = 60 * zoom;   // px per time unit
    const totalW     = Math.max(maxTime * timeScale + PAD_RIGHT, 200);
    const totalH     = signalList.length * ROW_H;

    // ── Cursor from click on waveform area
    const handleWaveClick = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const relX  = e.clientX - rect.left + scrollLeft;
        const t = Math.round(relX / timeScale);
        setCursor(Math.max(0, Math.min(t, maxTime)));
    }, [timeScale, scrollLeft, maxTime]);

    // ── Sync scroll between header, labels, and waves
    const handleScroll = useCallback((e) => {
        setScroll(e.currentTarget.scrollLeft);
    }, []);

    // ── Zoom helpers
    const zoomIn  = useCallback(() => setZoom(z => Math.min(z * 1.5, 40)), []);
    const zoomOut = useCallback(() => setZoom(z => Math.max(z / 1.5, 0.05)), []);
    const fitAll  = useCallback(() => {
        if (!containerRef.current || maxTime === 0) return;
        const w = containerRef.current.clientWidth - LABEL_W - PAD_RIGHT;
        setZoom(w / maxTime / 60);
    }, [maxTime]);

    // ── No data state
    if (!data || Object.keys(data.signals).length === 0) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                height: '100%', gap: '10px',
                color: '#64748b', background: 'var(--console-bg, #0d1117)'
            }}>
                <span style={{ fontSize: '28px' }}>📉</span>
                <strong style={{ fontSize: '13px', color: 'var(--text-main, #e2e8f0)' }}>No Waveform Data</strong>
                <p style={{ fontSize: '11px', margin: 0, maxWidth: '280px', textAlign: 'center', lineHeight: 1.5 }}>
                    Make sure your testbench uses <code>$dumpfile</code> and <code>$dumpvars</code> to generate a .vcd file.
                </p>
            </div>
        );
    }

    // ── Time tick marks
    const tickInterval = (() => {
        if (!maxTime || maxTime <= 0) return 10;
        const raw = maxTime / (Math.max(totalW / 80, 2));
        if (raw <= 0 || !isFinite(raw)) return 10;
        const log = Math.log10(raw);
        if (!isFinite(log)) return 10;
        const mag = Math.pow(10, Math.floor(log));
        for (const m of [1, 2, 5, 10, 20, 50, 100]) {
            if (raw <= m * mag) return Math.max(m * mag, 1);
        }
        return Math.max(mag * 100, 1);
    })();

    const ticks = [];
    const interval = tickInterval > 0 ? tickInterval : 10;
    for (let t = 0; t <= maxTime + interval; t += interval) {
        ticks.push(t);
        if (ticks.length > 1000) break; // Hard safety limit
    }

    const isLight = theme === 'light';
    const bg      = isLight ? '#f8fafc' : '#0d1117';
    const labelBg = isLight ? '#f1f5f9' : '#111827';
    const border  = isLight ? '#e2e8f0' : '#1e293b';
    const textCol = isLight ? '#0f172a' : '#e2e8f0';
    const mutedCol= isLight ? '#64748b' : '#64748b';
    const headerBg= isLight ? '#e2e8f0' : '#0a0f1a';

    return (
        <div style={{
            display: 'flex', flexDirection: 'column', height: '100%',
            background: bg, color: textCol, fontFamily: "'JetBrains Mono', monospace",
            fontSize: '11px', userSelect: 'none', overflow: 'hidden'
        }}>
            {/* ── Toolbar ─────────────────────────────────────────────── */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '6px 12px', borderBottom: `1px solid ${border}`,
                background: headerBg, flexShrink: 0
            }}>
                <span style={{ color: '#60a5fa', fontWeight: 'bold', fontSize: '11px', marginRight: 4 }}>
                    📈 {viewerType} ({formatType})
                </span>
                <div style={{ width: 1, height: 16, background: border, margin: '0 4px' }} />

                {/* Zoom buttons */}
                {[['−', zoomOut], ['+', zoomIn], ['Fit', fitAll]].map(([lbl, fn]) => (
                    <button key={lbl} onClick={fn} style={{
                        padding: '2px 8px', background: 'var(--border-color, #1e293b)',
                        border: `1px solid ${border}`, color: textCol,
                        borderRadius: 4, cursor: 'pointer', fontSize: '11px',
                        fontWeight: lbl === 'Fit' ? 'normal' : 'bold'
                    }}>{lbl}</button>
                ))}

                <span style={{ color: mutedCol, marginLeft: 4 }}>
                    {(zoom * 100).toFixed(0)}%
                </span>

                <div style={{ marginLeft: 'auto', color: mutedCol }}>
                    Max: <strong style={{ color: textCol }}>{maxTime} {unit}</strong>
                    {cursorTime !== null && (
                        <span style={{ marginLeft: 12, color: '#60a5fa' }}>
                            Cursor: <strong>{cursorTime} {unit}</strong>
                        </span>
                    )}
                </div>
            </div>

            {/* ── Body: label column + scrollable waveform area ───────── */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }} ref={containerRef}>

                {/* Fixed label column */}
                <div style={{
                    width: LABEL_W, flexShrink: 0, display: 'flex',
                    flexDirection: 'column', borderRight: `1px solid ${border}`,
                    background: labelBg, zIndex: 2
                }}>
                    {/* Header cell above time ruler */}
                    <div style={{
                        height: HEADER_H, borderBottom: `1px solid ${border}`,
                        display: 'flex', alignItems: 'center', paddingLeft: 10,
                        background: headerBg, color: mutedCol, fontSize: '10px',
                        fontWeight: 'bold', letterSpacing: '0.5px'
                    }}>
                        SIGNAL
                    </div>

                    {/* Signal rows */}
                    {signalList.map((sig, idx) => {
                        const color = SIGNAL_COLORS[idx % SIGNAL_COLORS.length];
                        const curVal = cursorTime !== null ? getValueAt(sig.values, cursorTime) : null;
                        const isVec = sig.width > 1;
                        const displayVal = curVal === null ? '' : isVec ? `0x${binToHex(curVal)}` : curVal;

                        return (
                            <div key={sig.symbol} style={{
                                height: ROW_H, borderBottom: `1px solid ${border}`,
                                display: 'flex', flexDirection: 'column',
                                justifyContent: 'center', padding: '0 10px',
                                gap: 2
                            }}>
                                <span style={{
                                    color, fontWeight: 'bold', fontSize: '11px',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                }}>
                                    {sig.name}
                                </span>
                                {cursorTime !== null && (
                                    <span style={{
                                        color: '#94a3b8', fontSize: '9px',
                                        fontFamily: 'monospace'
                                    }}>
                                        = {displayVal}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Scrollable waveform area */}
                <div
                    style={{ flex: 1, overflow: 'auto', position: 'relative' }}
                    onScroll={handleScroll}
                    ref={waveRef}
                >
                    <svg
                        width={totalW}
                        height={HEADER_H + totalH + 4}
                        onClick={handleWaveClick}
                        style={{ display: 'block', cursor: 'crosshair' }}
                    >
                        {/* Background grid lines */}
                        {ticks.map(t => (
                            <line
                                key={t}
                                x1={t * timeScale} x2={t * timeScale}
                                y1={HEADER_H} y2={HEADER_H + totalH}
                                stroke={border} strokeWidth="0.5"
                            />
                        ))}

                        {/* ── Time ruler ───────────────────────────── */}
                        <rect x={0} y={0} width={totalW} height={HEADER_H}
                            fill={headerBg} />
                        {ticks.map(t => (
                            <g key={t} transform={`translate(${t * timeScale}, 0)`}>
                                <line y1={HEADER_H - 8} y2={HEADER_H}
                                    stroke={border} strokeWidth="1" />
                                <text
                                    x={0} y={HEADER_H - 12}
                                    fontSize="9" textAnchor="middle"
                                    fill={mutedCol}
                                >
                                    {t}
                                </text>
                            </g>
                        ))}
                        <text x={4} y={HEADER_H - 2} fontSize="8" fill={mutedCol}>
                            {unit}
                        </text>

                        {/* ── Signal rows ───────────────────────────── */}
                        {signalList.map((sig, idx) => {
                            const color  = SIGNAL_COLORS[idx % SIGNAL_COLORS.length];
                            const yBase  = HEADER_H + idx * ROW_H;
                            const yHigh  = yBase + 6;
                            const yLow   = yBase + ROW_H - 8;
                            const yMid   = yBase + ROW_H / 2;
                            const isVec  = sig.width > 1;

                            return (
                                <g key={sig.symbol}>
                                    {/* Row separator */}
                                    <line x1={0} x2={totalW}
                                        y1={yBase + ROW_H} y2={yBase + ROW_H}
                                        stroke={border} strokeWidth="0.5" />

                                    {/* Waveform segments */}
                                    {sig.values.map((v, i) => {
                                        const t1    = v.time;
                                        const t2    = sig.values[i + 1]?.time ?? maxTime;
                                        const x1    = t1 * timeScale;
                                        const x2    = t2 * timeScale;
                                        const segW  = x2 - x1;
                                        const isX   = /[xX]/.test(v.val);
                                        const isZ   = /[zZ]/.test(v.val);
                                        const isUnk = isX || isZ;
                                        const segColor = isUnk ? '#f59e0b' : color;

                                        if (!isVec) {
                                            // Scalar — digital waveform
                                            const isHigh = v.val === '1';
                                            const yThis  = isHigh ? yHigh : yLow;
                                            const yNext  = sig.values[i + 1]?.val === '1' ? yHigh : yLow;
                                            return (
                                                <React.Fragment key={i}>
                                                    {isUnk ? (
                                                        <rect x={x1} y={yHigh} width={segW}
                                                            height={yLow - yHigh}
                                                            fill="rgba(245,158,11,0.15)"
                                                            stroke="#f59e0b" strokeWidth="0.5" />
                                                    ) : (
                                                        <line x1={x1} x2={x2}
                                                            y1={yThis} y2={yThis}
                                                            stroke={segColor} strokeWidth="2" />
                                                    )}
                                                    {/* Transition */}
                                                    {i < sig.values.length - 1 && !isUnk && (
                                                        <line x1={x2} x2={x2}
                                                            y1={yThis} y2={yNext}
                                                            stroke={segColor} strokeWidth="1.5" />
                                                    )}
                                                </React.Fragment>
                                            );
                                        } else {
                                            // Vector / bus — trapezoidal box
                                            const hexVal = `0x${binToHex(v.val)}`;
                                            const trap   = Math.min(6, segW / 4);
                                            const path   = [
                                                `M ${x1} ${yMid}`,
                                                `L ${x1 + trap} ${yHigh}`,
                                                `L ${x2 - trap} ${yHigh}`,
                                                `L ${x2} ${yMid}`,
                                                `L ${x2 - trap} ${yLow}`,
                                                `L ${x1 + trap} ${yLow}`,
                                                'Z'
                                            ].join(' ');
                                            return (
                                                <g key={i}>
                                                    <path d={path}
                                                        fill={isUnk ? 'rgba(245,158,11,0.12)' : `${segColor}18`}
                                                        stroke={segColor} strokeWidth="1.5" />
                                                    {segW > 30 && (
                                                        <text
                                                            x={x1 + segW / 2} y={yMid + 3.5}
                                                            fontSize="9" textAnchor="middle"
                                                            fill={segColor} fontWeight="bold"
                                                        >
                                                            {hexVal}
                                                        </text>
                                                    )}
                                                </g>
                                            );
                                        }
                                    })}
                                </g>
                            );
                        })}

                        {/* ── Cursor ───────────────────────────────── */}
                        {cursorTime !== null && (
                            <g transform={`translate(${cursorTime * timeScale}, 0)`}>
                                <line
                                    y1={0} y2={HEADER_H + totalH}
                                    stroke="#ef4444" strokeWidth="1.5"
                                    strokeDasharray="4 3"
                                />
                                {/* Cursor handle at top */}
                                <polygon
                                    points="0,0 -5,6 5,6"
                                    fill="#ef4444"
                                />
                                <rect x={-24} y={7} width={48} height={14}
                                    rx={3} fill="#ef4444" />
                                <text x={0} y={18}
                                    fontSize="8" textAnchor="middle"
                                    fill="#fff" fontWeight="bold">
                                    {cursorTime}{unit}
                                </text>
                            </g>
                        )}
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default WaveformViewer;
